import React from 'react';
import { Trophy, Lock, Sparkles, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { COSMIC_RANKS, HIDDEN_RANK } from '../mock/mock';
import { Card } from '../components/ui/card';
import RankMedal from '../components/RankMedal';

export default function Ranks() {
  const { user } = useAuth();
  if (!user) return null;
  const current = COSMIC_RANKS.find(r => r.rank === user.rank) || COSMIC_RANKS[0];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-2xl md:text-3xl">Cosmic Rank System</h1>
        <p className="text-white/60 mt-1 text-sm">Earn XP through chat, gifts, games and missions to rise through 12 legendary cosmic ranks.</p>
      </div>

      {/* Current rank hero card — shows the actual medal artwork at a large size */}
      <Card className="bg-[#161622] border-white/10 overflow-hidden">
        <div className="relative" style={{ background: 'linear-gradient(135deg, rgba(123,47,247,0.3), rgba(0,198,255,0.25), rgba(0,245,212,0.2))' }}>
          <div className="flex items-center gap-4 px-5 py-5">
            <RankMedal rank={user.rank} size={160} data-testid="ranks-current-medal" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-white/70">Your current rank</div>
              <div className="font-display font-bold text-xl md:text-2xl flex items-center gap-2 mt-1" style={{ color: current.color }}>
                <Trophy size={20} /> {current.name}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-wider text-white/70">Total XP</div>
              <div className="font-display font-bold text-lg md:text-xl flex items-center gap-1 text-cyan-300"><Zap size={16} fill="currentColor" />{user.xp?.toLocaleString() || 0}</div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {COSMIC_RANKS.map(r => {
          const unlocked = (user.xp || 0) >= (r.xp || 0);
          const isCurrent = r.rank === user.rank;
          return (
            <div
              key={r.rank}
              data-testid={`ranks-card-${r.rank}`}
              className="relative rounded-2xl p-4 border flex flex-col"
              style={{
                background: unlocked ? `linear-gradient(180deg, ${r.color}18 0%, rgba(255,255,255,0.02) 100%)` : 'rgba(255,255,255,0.02)',
                borderColor: unlocked ? r.color + '55' : 'rgba(255,255,255,0.08)',
                boxShadow: isCurrent ? `0 0 0 2px ${r.color}, 0 0 24px ${r.color}55` : (unlocked ? `0 0 18px ${r.color}22` : 'none'),
              }}
            >
              {isCurrent && (
                <span
                  className="absolute -top-2 left-4 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider z-10"
                  style={{ background: r.color, color: '#0A0A0F' }}
                  data-testid={`ranks-current-badge-${r.rank}`}
                >
                  Current
                </span>
              )}

              {/* Full-width medal artwork — prominent, clearly readable */}
              <div className="relative w-full" style={{ opacity: unlocked ? 1 : 0.55 }}>
                <RankMedal
                  rank={r.rank}
                  size={280}
                  animated={isCurrent}
                  className="!w-full"
                  data-testid={`ranks-medal-${r.rank}`}
                />
                {!unlocked && (
                  <div
                    className="absolute inset-0 rounded-xl flex items-center justify-center bg-black/45 backdrop-blur-[1px]"
                    aria-label="Locked"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0a0a15]/90 border border-white/25 flex items-center justify-center text-white/85">
                      <Lock size={16} />
                    </div>
                  </div>
                )}
              </div>

              {/* Meta */}
              <div className="mt-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] text-white/40">Rank {r.rank}</div>
                  <div className="font-display font-bold mt-0.5 truncate text-base" style={{ color: r.color }}>{r.name}</div>
                </div>
                <div className="text-xs text-cyan-300 flex items-center gap-1 shrink-0 whitespace-nowrap pt-0.5"><Zap size={11} fill="currentColor" />{(r.xp || 0).toLocaleString()} XP</div>
              </div>

              <ul className="mt-2.5 space-y-1">
                {r.benefits.map(b => (
                  <li key={b} className="text-[11px] text-white/70 flex items-start gap-2">
                    <Sparkles size={9} className="mt-0.5 flex-shrink-0" style={{ color: r.color }} /> {b}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {/* Hidden rank teaser */}
        <div className="relative rounded-2xl p-4 pt-5 border border-fuchsia-500/30 flex flex-col" style={{ background: 'radial-gradient(circle at top right, rgba(192,132,252,0.2), rgba(123,47,247,0.05))' }}>
          <div className="absolute -top-2 left-4 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-fuchsia-500 text-white z-10">Legendary</div>
          <div
            className="relative w-full rounded-xl overflow-hidden flex items-center justify-center"
            style={{
              aspectRatio: '256 / 143',
              background: 'radial-gradient(circle at 50% 50%, rgba(192,132,252,0.25) 0%, transparent 65%), #0a0a15',
              border: '1.5px solid rgba(192,132,252,0.55)',
              boxShadow: '0 0 22px rgba(192,132,252,0.35)',
            }}
          >
            <Sparkles size={64} className="text-fuchsia-300 opacity-90" />
          </div>
          <div className="mt-3">
            <div className="text-[11px] text-white/40">Hidden Rank</div>
            <div className="font-display font-bold mt-0.5 text-fuchsia-300 text-base">{HIDDEN_RANK.name}</div>
          </div>
          <p className="mt-2 text-[11px] text-white/70">{HIDDEN_RANK.description}</p>
        </div>
      </div>
    </div>
  );
}
